import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, FileText, Package, Users, CalendarClock, LifeBuoy } from "lucide-react";
import { Button } from "@finora/ui";
import { api } from "../../../lib/api";
import type { SelectedPackage } from "./PackageStep";

/**
 * Onboarding, korak "Završeno" - summary sa STVARNIM podacima (GET /company,
 * GET /users), ne rekapitulacijom lokalnog state-a iz prethodnih koraka
 * (koji bi mogao da se raziđe od onoga što je stvarno sačuvano na
 * backend-u). "Pogledajte vodič" vodi na Finora podršku (Zendesk help
 * center za fiskalizaciju), otvara se u novom tabu.
 */
const SUPPORT_GUIDE_URL = "https://podrska.hafiskal.rs/hc/fiskal/sr";

export function CompleteStep({ selectedPackage }: { selectedPackage: SelectedPackage | null }) {
  const navigate = useNavigate();
  const { data: settings } = useQuery({
    queryKey: ["company-settings-onboarding-complete"],
    queryFn: () => api.getCompanySettings(),
  });
  const { data: users } = useQuery({
    queryKey: ["users-onboarding-complete"],
    queryFn: () => api.listUsers(),
  });

  return (
    <div className="rounded-xl border border-border bg-surface p-8 text-center shadow-subtle">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
        <CheckCircle2 className="h-9 w-9" />
      </div>
      <h1 className="mb-2 text-2xl font-bold text-slate-900">Čestitamo!</h1>
      <p className="mb-8 text-sm text-slate-500">
        Vaš HAFiskal je uspešno podešen. Sada možete da koristite sve izabrane module i da započnete sa radom.
      </p>

      <div className="mx-auto mb-8 grid max-w-2xl gap-4 text-left md:grid-cols-2">
        <div className="flex items-start gap-3 rounded-lg border border-border bg-app-bg p-4">
          <FileText className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand" />
          <div>
            <p className="text-sm font-medium text-slate-900">Kompanija</p>
            <p className="text-sm text-slate-500">{settings?.name ?? "—"}</p>
            <p className="text-xs text-slate-400">PIB: {settings?.taxId ?? "—"}</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-border bg-app-bg p-4">
          <Users className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand" />
          <div>
            <p className="text-sm font-medium text-slate-900">Korisnici</p>
            <p className="text-sm text-slate-500">{users?.length ?? 0} korisnika</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-border bg-app-bg p-4">
          <Package className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand" />
          <div>
            <p className="text-sm font-medium text-slate-900">Paket</p>
            <p className="text-sm text-slate-500">{selectedPackage?.name ?? "—"}</p>
            <p className="text-xs text-slate-400">{selectedPackage?.includedModules.length ?? 0} modula</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-border bg-app-bg p-4">
          <CalendarClock className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand" />
          <div>
            <p className="text-sm font-medium text-slate-900">Probni period</p>
            <p className="text-sm text-slate-500">
              {selectedPackage?.isInstantTrial && selectedPackage.trialDurationDays
                ? `${selectedPackage.trialDurationDays} dana`
                : "Paket se aktivira po naplati fakture"}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center gap-3 sm:flex-row">
        <Button
          variant="secondary"
          onClick={() => window.open(SUPPORT_GUIDE_URL, "_blank", "noopener,noreferrer")}
        >
          <LifeBuoy className="h-4 w-4" /> Pogledajte vodič
        </Button>
        <Button onClick={() => navigate("/", { replace: true })}>Idi na dashboard →</Button>
      </div>
    </div>
  );
}
